import { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { api } from "@/lib/api";
import { date as fmtDate } from "@/lib/format";
import { Button, Modal, PageHeader, Pill, Table, Td, Th, Input } from "@/components/ui";
import { confirmPopup } from "@/components/popup";

interface PromoCode {
  id: number;
  code: string;
  discount_percent: number;
  kind: string;
  value: number;
  product: string;
  ttl_hours: number;
  max_uses: number | null;
  used_count: number;
  expires_at: string | null;
  is_active: boolean;
  created_at: string;
}

interface PromoCodeListResponse {
  items: PromoCode[];
  total: number;
}

interface UsageItem {
  id: number;
  user_id: number;
  used_at: string;
  original_amount: number;
  discount_amount: number;
  effect_kind?: string | null;
  effect_value?: number | null;
  effect_product?: string | null;
  bought_type?: string | null;
  bought_days?: number | null;
}

const BOUGHT_RU: Record<string, string> = {
  proxy_purchase: "прокси",
  vpn_purchase: "VPN",
  vpn_devices: "устройства",
  traffic_purchase: "трафик",
  balance_topup: "пополнение",
};

function boughtLabel(u: UsageItem): string | null {
  if (!u.bought_type) return null;
  const what = BOUGHT_RU[u.bought_type] ?? u.bought_type;
  return u.bought_days ? `${what} · ${u.bought_days} дн.` : what;
}

const PRODUCT_RU: Record<string, string> = { vpn: "VPN", proxy: "прокси", any: "" };

// Что человек получил. У кодов с днями платежа нет, и раньше такие строки
// выглядели как «0,00 ₽ / -0,00 ₽» — неотличимо от скидки, которую ещё не
// выкупили. Теперь тип виден явно.
function usageEffect(u: UsageItem): { label: string; tone: string } {
  const prod = PRODUCT_RU[u.effect_product ?? "any"] ?? "";
  const suffix = prod ? ` · ${prod}` : "";
  if (u.effect_kind === "days") {
    const d = Math.round(u.effect_value ?? 0);
    return { label: `+${d} ${plural(d, "день", "дня", "дней")}${suffix}`, tone: "text-sky-400" };
  }
  if (u.effect_kind === "topup_bonus") {
    return { label: `бонус ${fmtPct(u.effect_value)}%${suffix}`, tone: "text-violet-400" };
  }
  // Скидку забрали, но ещё не выкупили: платежа нет, зато процент известен.
  // Пишем его так же, как у выкупленной — разницу показывает колонка оплаты
  // («ждёт покупки»), а не малопонятная формулировка в этой.
  if (u.effect_kind === "discount_claimed") {
    return { label: `скидка${u.effect_value ? ` ${fmtPct(u.effect_value)}%` : ""}${suffix}`,
             tone: "text-amber-400" };
  }
  if (u.effect_kind === "discount" || u.discount_amount > 0) {
    // Процент показываем только правдоподобный: в истории попадались записи
    // с битыми суммами, из которых выходило «-1121%».
    const raw = u.effect_value ?? (u.original_amount
      ? Math.round((u.discount_amount * 100) / u.original_amount) : null);
    const pct = raw !== null && raw > 0 && raw <= 100 ? raw : null;
    return {
      label: `скидка${pct ? ` ${fmtPct(pct)}%` : ""}${suffix}`,
      tone: "text-emerald-400",
    };
  }
  return { label: "—", tone: "text-zinc-600" };
}

function fmtPct(v: number | null | undefined): string {
  if (v == null) return "";
  return Number.isInteger(v) ? String(v) : String(Math.round(v * 10) / 10);
}

function plural(n: number, one: string, few: string, many: string): string {
  const m10 = n % 10, m100 = n % 100;
  if (m10 === 1 && m100 !== 11) return one;
  if (m10 >= 2 && m10 <= 4 && (m100 < 10 || m100 >= 20)) return few;
  return many;
}

interface CreateForm {
  code: string;
  kind: string;
  value: string;
  product: string;
  ttl_hours: string;
  discount_percent: string;
  max_uses: string;
  expires_at: string;
}

const EMPTY_FORM: CreateForm = {
  code: "",
  kind: "discount",
  value: "10",
  product: "any",
  ttl_hours: "24",
  discount_percent: "10",
  max_uses: "",
  expires_at: "",
};

const KIND_LABELS: Record<string, string> = {
  discount: "Скидка на покупку, %",
  days: "Дни подписки",
  topup_bonus: "Бонус к пополнению, %",
  choice: "Выбор юзера (2-3 варианта)",
};
const KIND_SHORT: Record<string, string> = {
  discount: "скидка", days: "дни", topup_bonus: "бонус", choice: "выбор",
};

interface ChoiceOpt { kind: string; value: string; product: string }
// product=any: скидка по умолчанию на всё. Было "vpn" — и у варианта «скидка»
// селектора продукта нет, так что дефолт улетал молча, а скидка не применялась
// к прокси (жалоба 19.10.2026).
const EMPTY_OPT: ChoiceOpt = { kind: "discount", value: "", product: "any" };

const rub = new Intl.NumberFormat("ru-RU", { style: "currency", currency: "RUB" });
const dt = (s: string | null) => (s ? fmtDate(s) : "—");

export default function PromoCodes() {
  const qc = useQueryClient();
  const [createOpen, setCreateOpen] = useState(false);
  const [usagesPromo, setUsagesPromo] = useState<PromoCode | null>(null);
  const [form, setForm] = useState<CreateForm>(EMPTY_FORM);
  const [opts, setOpts] = useState<ChoiceOpt[]>([{ ...EMPTY_OPT }, { ...EMPTY_OPT, kind: "days" }]);

  const q = useQuery<PromoCodeListResponse>({
    queryKey: ["promo-codes"],
    queryFn: () => api("/api/admin/promo-codes"),
  });

  const usagesQ = useQuery<{ items: UsageItem[]; total?: number }>({
    queryKey: ["promo-usages", usagesPromo?.id],
    queryFn: () => api(`/api/admin/promo-codes/${usagesPromo!.id}/usages?limit=500`),
    enabled: !!usagesPromo,
  });

  const create = useMutation({
    mutationFn: () =>
      api("/api/admin/promo-codes", {
        method: "POST",
        body: JSON.stringify({
          code: form.code.trim().toUpperCase(),
          kind: form.kind,
          value: Number(form.value),
          product: form.kind === "days" ? form.product : (form.product || "any"),
          ttl_hours: Number(form.ttl_hours) || 0,
          options: form.kind === "choice"
            ? opts.filter((o) => Number(o.value) > 0).map((o) => ({
                kind: o.kind, value: Number(o.value), product: o.product,
              }))
            : null,
          discount_percent: form.kind === "discount" ? Number(form.value) : 0,
          max_uses: form.max_uses ? Number(form.max_uses) : null,
          expires_at: form.expires_at || null,
        }),
      }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["promo-codes"] });
      setCreateOpen(false);
      setForm(EMPTY_FORM);
    },
  });

  const deactivate = useMutation({
    mutationFn: (id: number) =>
      api(`/api/admin/promo-codes/${id}/deactivate`, { method: "POST" }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["promo-codes"] }),
  });

  const remove = useMutation({
    mutationFn: (id: number) =>
      api(`/api/admin/promo-codes/${id}`, { method: "DELETE" }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["promo-codes"] }),
  });

  const items = q.data?.items ?? [];

  const upd = <K extends keyof CreateForm>(k: K, v: CreateForm[K]) =>
    setForm((f) => ({ ...f, [k]: v }));

  return (
    <div className="p-4 md:p-8">
      <PageHeader
        title="Промокоды"
        right={
          <div className="flex items-center gap-2 sm:gap-4 flex-wrap">
            <div className="text-zinc-400 text-sm">всего: {q.data?.total ?? 0}</div>
            <Button variant="primary" onClick={() => setCreateOpen(true)}>
              + Создать
            </Button>
          </div>
        }
      />

      {q.isLoading && <div className="text-zinc-400">Загрузка…</div>}
      {q.isError && <div className="text-red-400">Ошибка загрузки</div>}

      {items.length > 0 && (
        <Table>
          <thead>
            <tr>
              <Th>код</Th>
              <Th>скидка</Th>
              <Th>использовано</Th>
              <Th>истекает</Th>
              <Th>статус</Th>
              <Th fit>{" "}</Th>
            </tr>
          </thead>
          <tbody>
            {items.map((p) => (
              <tr
                key={p.id}
                className="hover:bg-zinc-900/50 cursor-pointer"
                onClick={() => setUsagesPromo(p)}
              >
                <Td className="font-mono text-zinc-100">{p.code}</Td>
                <Td className="text-emerald-400">
                  {(p.kind ?? "discount") === "discount" ? `${p.discount_percent}%`
                    : p.kind === "days" ? `+${p.value} дн (${p.product})`
                    : p.kind === "choice" ? "выбор юзера"
                    : `+${p.value}% топап`}
                  <span className="text-[10px] text-zinc-500 ml-1">{KIND_SHORT[p.kind ?? "discount"]}</span>
                </Td>
                <Td className="text-zinc-300">
                  {p.used_count}
                  {p.max_uses != null ? `/${p.max_uses}` : ""}
                </Td>
                <Td>{dt(p.expires_at)}</Td>
                <Td>
                  {p.is_active ? (
                    <Pill tone="success">активен</Pill>
                  ) : (
                    <Pill tone="danger">неактивен</Pill>
                  )}
                </Td>
                <Td className="flex gap-2">
                  {p.is_active && (
                    <Button
                      variant="ghost"
                      onClick={(e) => {
                        e.stopPropagation();
                        deactivate.mutate(p.id);
                      }}
                    >
                      Деактивировать
                    </Button>
                  )}
                  <Button
                    variant="danger"
                    onClick={async (e) => {
                      e.stopPropagation();
                      if (await confirmPopup({ title: "Удалить промокод?", body: p.code, danger: true, confirmLabel: "Удалить" })) remove.mutate(p.id);
                    }}
                  >
                    Удалить
                  </Button>
                </Td>
              </tr>
            ))}
          </tbody>
        </Table>
      )}

      {/* Create modal */}
      <Modal open={createOpen} onClose={() => setCreateOpen(false)} title="Создать промокод">
        <div className="space-y-4">
          <div className="grid grid-cols-2 gap-4">
            <Field label="код">
              <Input
                value={form.code}
                placeholder="SUMMER20"
                onChange={(e) => upd("code", e.target.value)}
                autoFocus
              />
            </Field>
            <Field label="тип кода">
              <div className="flex flex-wrap gap-1.5">
                {Object.entries(KIND_LABELS).map(([k]) => (
                  <button
                    key={k}
                    onClick={() => upd("kind", k)}
                    className={`h-8 px-2.5 rounded-lg text-xs transition ${
                      form.kind === k ? "bg-brand-soft text-brand glow-ring" : "bg-white/[0.04] text-zinc-400 hover:text-zinc-200"
                    }`}
                  >
                    {KIND_SHORT[k]}
                  </button>
                ))}
              </div>
            </Field>
            {form.kind !== "choice" && (
              <Field label={KIND_LABELS[form.kind]}>
                <Input
                  value={form.value}
                  type="number"
                  min={1}
                  onChange={(e) => upd("value", e.target.value)}
                />
              </Field>
            )}
            {form.kind === "choice" && (
              <div className="col-span-2 space-y-2">
                <div className="text-xs text-zinc-500">Варианты (юзер берёт один):</div>
                {opts.map((o, i) => (
                  <div key={i} className="flex items-center gap-2 flex-wrap">
                    {["discount", "days", "topup_bonus"].map((k) => (
                      <button
                        key={k}
                        onClick={() => setOpts((p) => p.map((x, j) => (j === i
                          ? { ...x, kind: k, product: k === "days" ? (x.product === "any" ? "vpn" : x.product) : x.product }
                          : x)))}
                        className={`h-7 px-2 rounded-lg text-[11px] transition ${
                          o.kind === k ? "bg-brand-soft text-brand" : "bg-white/[0.04] text-zinc-500"
                        }`}
                      >
                        {KIND_SHORT[k]}
                      </button>
                    ))}
                    <Input
                      value={o.value}
                      type="number"
                      placeholder="число"
                      className="w-24"
                      onChange={(e) => setOpts((p) => p.map((x, j) => (j === i ? { ...x, value: e.target.value } : x)))}
                    />
                    {(o.kind === "days" || o.kind === "discount") && (
                      <div className="flex gap-1">
                        {(o.kind === "days"
                          ? [["vpn", "VPN"], ["proxy", "прокси"]]
                          : [["any", "всё"], ["vpn", "VPN"], ["proxy", "прокси"]]).map(([k, l]) => (
                          <button
                            key={k}
                            onClick={() => setOpts((p) => p.map((x, j) => (j === i ? { ...x, product: k } : x)))}
                            className={`h-7 px-2 rounded-lg text-[11px] transition ${
                              o.product === k ? "bg-brand-soft text-brand" : "bg-white/[0.04] text-zinc-500"
                            }`}
                          >
                            {l}
                          </button>
                        ))}
                      </div>
                    )}
                    {opts.length > 2 && (
                      <button className="text-zinc-600 hover:text-red-400 text-xs"
                              onClick={() => setOpts((p) => p.filter((_, j) => j !== i))}>✕</button>
                    )}
                  </div>
                ))}
                {opts.length < 3 && (
                  <button className="text-xs text-brand hover:underline"
                          onClick={() => setOpts((p) => [...p, { ...EMPTY_OPT, kind: "topup_bonus" }])}>
                    + вариант
                  </button>
                )}
              </div>
            )}
            {form.kind === "days" && (
              <Field label="продукт (для дней)">
                <div className="flex gap-1.5">
                  {[["vpn", "VPN"], ["proxy", "Прокси"]].map(([k, l]) => (
                    <button
                      key={k}
                      onClick={() => upd("product", k)}
                      className={`h-8 px-2.5 rounded-lg text-xs transition ${
                        form.product === k ? "bg-brand-soft text-brand glow-ring" : "bg-white/[0.04] text-zinc-400 hover:text-zinc-200"
                      }`}
                    >
                      {l}
                    </button>
                  ))}
                </div>
              </Field>
            )}
            {form.kind !== "days" && (
              <Field label="сгорает через N часов после активации">
                <Input
                  value={form.ttl_hours}
                  type="number"
                  min={1}
                  onChange={(e) => upd("ttl_hours", e.target.value)}
                />
              </Field>
            )}
            <Field label="макс. использований (пусто = без лимита)">
              <Input
                value={form.max_uses}
                type="number"
                min={1}
                placeholder="без лимита"
                onChange={(e) => upd("max_uses", e.target.value)}
              />
            </Field>
            <Field label="истекает (пусто = бессрочно)">
              <Input
                value={form.expires_at}
                type="datetime-local"
                onChange={(e) => upd("expires_at", e.target.value)}
              />
            </Field>
          </div>
          {create.isError && (
            <div className="text-xs text-red-400">{(create.error as Error).message}</div>
          )}
          <div className="flex gap-3 justify-end pt-2">
            <Button variant="ghost" onClick={() => setCreateOpen(false)}>
              Отмена
            </Button>
            <Button
              variant="primary"
              disabled={!form.code.trim() || create.isPending}
              onClick={() => create.mutate()}
            >
              {create.isPending ? "Создаём…" : "Создать"}
            </Button>
          </div>
        </div>
      </Modal>

      {/* Usages modal */}
      <Modal
        open={!!usagesPromo}
        onClose={() => setUsagesPromo(null)}
        title={`Использования: ${usagesPromo?.code}`}
        maxWidth="max-w-3xl"
      >
        {usagesQ.isLoading && <div className="text-zinc-400">Загрузка…</div>}
        {usagesQ.data && usagesQ.data.items.length === 0 && (
          <div className="text-zinc-500 text-sm">Пока никто не использовал</div>
        )}
        {usagesQ.data && usagesQ.data.items.length > 0 && (
          <>
            <div className="text-xs text-zinc-500 mb-2">
              {(() => {
                const shown = usagesQ.data.items.length;
                const total = usagesQ.data.total ?? shown;
                return total > shown
                  ? `показаны последние ${shown} из ${total}`
                  : `всего ${total}`;
              })()}
            </div>
            <div className="max-h-[60vh] overflow-y-auto">
          <Table>
            <thead>
              <tr>
                <Th>user_id</Th>
                <Th>дата</Th>
                <Th>получил</Th>
                <Th>купил</Th>
                <Th>оплата</Th>
              </tr>
            </thead>
            <tbody>
              {usagesQ.data.items.map((u) => {
                const eff = usageEffect(u);
                const paid = u.original_amount > 0;
                return (
                  <tr key={u.id}>
                    <Td className="text-zinc-300">{u.user_id}</Td>
                    <Td className="text-zinc-400 text-xs">
                      {new Date(u.used_at).toLocaleString("ru-RU")}
                    </Td>
                    <Td className={eff.tone}>{eff.label}</Td>
                    <Td className="text-zinc-300">
                      {boughtLabel(u) ?? <span className="text-zinc-600">—</span>}
                    </Td>
                    <Td className="text-zinc-400">
                      {paid ? (
                        <span>
                          <span className="text-zinc-500 line-through mr-1.5">
                            {rub.format(u.original_amount)}
                          </span>
                          <span className="text-zinc-200">
                            {rub.format(u.original_amount - u.discount_amount)}
                          </span>
                        </span>
                      ) : (
                        <span className="text-zinc-600">
                          {u.effect_kind === "discount_claimed" ? "ждёт покупки" : "—"}
                        </span>
                      )}
                    </Td>
                  </tr>
                );
              })}
            </tbody>
          </Table>
            </div>
          </>
        )}
      </Modal>
    </div>
  );
}

function Field({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div>
      <div className="text-xs text-zinc-500 mb-1">{label}</div>
      {children}
    </div>
  );
}
